import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { validateChartRequest, formatValidationErrors } from "@/lib/chart-validators";
import { z } from "zod";

interface ChartPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chart: any | null;
}

export function ChartPreviewDialog({ open, onOpenChange, chart }: ChartPreviewDialogProps) {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Generate preview when dialog opens or chart changes
  useEffect(() => {
    if (open && chart) {
      generatePreview();
    } else {
      // Cleanup: revoke object URL when closing
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
        setImageUrl(null);
      }
    }
  }, [open, chart]);

  const generatePreview = async () => {
    if (!chart) return;

    setLoading(true);
    setError(null);
    setValidationErrors([]);

    try {
      // Build chart data from configuration
      const chartData = buildChartData(chart);

      console.log("🎨 Generating chart preview modal:", {
        type: chart.chart_type,
        data: chartData,
      });

      // Preparar el request completo
      let chartRequest: any;

      // Para bar-referral y bar, buildChartData retorna {data, options}
      if (chart.chart_type === "bar-referral" || (chart.chart_type === "bar" && typeof chartData === 'object' && 'data' in chartData)) {
        chartRequest = {
          type: chart.chart_type,
          data: (chartData as any).data,
          options: (chartData as any).options || chart.chart_config.options || {},
        };
      } else {
        // Para otros tipos (radial, pie, etc), chartData ya es el array directo
        chartRequest = {
          type: chart.chart_type,
          data: chartData,
          options: chart.chart_config.options || {},
        };
      }

      // VALIDAR ANTES DE ENVIAR
      const validation = validateChartRequest(chartRequest);

      if (!validation.success) {
        setValidationErrors(validation.errors.map(e => `${e.path}: ${e.message}`));
        setError("El gráfico tiene errores de validación. Revisa los detalles abajo.");
        toast.error("Error de validación en el gráfico");
        return;
      }

      console.log("✅ Validación exitosa, enviando request:", validation.data);

      // Call generate-chart edge function using fetch to handle binary properly
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-chart`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(validation.data),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText}`);
      }

      // Get the PNG blob from response
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setImageUrl(url);
      toast.success("Gráfico generado correctamente");
    } catch (err: any) {
      console.error("Error generating chart preview:", err);
      setError(err.message || "Error al generar vista previa");
      toast.error("Error al generar vista previa del gráfico");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to resolve a value for preview
  const resolveValueForPreview = (valueConfig: any): number => {
    // If it's a direct number/string (legacy format)
    if (typeof valueConfig === 'number') return Math.round(valueConfig * 100) / 100;
    if (typeof valueConfig === 'string') {
      const parsed = parseFloat(valueConfig) || 0;
      return Math.round(parsed * 100) / 100;
    }

    // If it's the new format {type, value}
    if (valueConfig && typeof valueConfig === 'object' && 'type' in valueConfig) {
      if (valueConfig.type === 'number') {
        const parsed = parseFloat(valueConfig.value) || 0;
        return Math.round(parsed * 100) / 100;
      }
      // For variables/calculations, use random sample for preview (with 2 decimals)
      const randomValue = 5 + Math.random() * 5;
      return Math.round(randomValue * 100) / 100;
    }

    return 0;
  };

  // Build chart data from configuration
  const buildChartData = (chart: any) => {
    const config = chart.chart_config || {};
    const colors = config.colors || [];
    const dataParams = config.data_params || [];
    const labels = config.labels || {};

    console.log("📊 Building chart data:", { config, dataParams, chartType: chart.chart_type });

    // Build data based on chart type
    if (chart.chart_type === "radial") {
      const data = dataParams.map((category: any) => {
        // If already in new format with dimensions
        if (category.dimensions && Array.isArray(category.dimensions)) {
          return {
            name: category.name,
            color: category.color,
            dimensions: category.dimensions.map((dim: any) => ({
              name: dim.name,
              barValue: resolveValueForPreview(dim.barValue),
              lineValue: resolveValueForPreview(dim.lineValue),
            })),
          };
        }

        // If old format {label, value, color}, convert to new format
        if (category.label !== undefined) {
          return {
            name: category.label,
            color: category.color || "#000000",
            dimensions: [{
              name: "Valor",
              barValue: parseFloat(category.value) || 0,
              lineValue: parseFloat(category.value) || 0,
            }],
          };
        }

        return category;
      });

      return data;
    }

    if (chart.chart_type === "bar-referral") {
      // For bar-referral, cada parámetro contiene userValue (value) y referralValue (value2)
      const data = dataParams.map((param: any, index: number) => {
        const userValue = param.type === "number"
          ? parseFloat(param.value) || 0
          : Math.floor(3 + Math.random() * 5); // Número entero entre 3-7

        const referralValue = param.type2 === "number"
          ? parseFloat(param.value2) || 0
          : parseFloat((3 + Math.random() * 5).toFixed(2)); // Número decimal entre 3.00-8.00 con máximo 2 decimales

        return {
          label: param.label || ` `,
          userValue,
          referralValue,
        };
      });

      return {
        data,
        options: {
          userColor: colors[0] || "#7C3AED",
          referralColor: colors[1] || "#10B981",
          userLabel: labels.userLabel || "Usuario",
          referralLabel: labels.referralLabel || "Referido",
        },
      };
    }

    // For other chart types
    const data = dataParams.map((param: any, index: number) => {
      // Use resolveValueForPreview to handle both old and new format
      const value = resolveValueForPreview(param.value);

      const item: any = {
        label: param.label || `Item ${index + 1}`,
        value: value,
      };

      // Add color if available
      if (colors[index]) {
        item.color = colors[index];
      }

      return item;
    });

    // Add specific options for bar chart
    if (chart.chart_type === "bar" && colors.length > 0) {
      return {
        data,
        options: {
          barColor: colors[0],
          barBackgroundColor: "transparent", // Sin fondo de barra (como bar-referral)
        },
      };
    }

    return data;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Vista Previa del Gráfico</DialogTitle>
          <DialogDescription>
            {chart?.name} - {chart?.chart_type?.toUpperCase()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Slide {chart?.slide_number} - Posición: ({chart?.position_x}, {chart?.position_y})
            </div>
            <Button variant="outline" size="sm" onClick={generatePreview} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Regenerar
            </Button>
          </div>

          <div className="border rounded-lg p-4 bg-muted/30 min-h-[400px] flex items-center justify-center">
            {loading && (
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-sm text-muted-foreground">Generando gráfico...</p>
              </div>
            )}

            {error && !loading && (
              <div className="text-center w-full max-w-2xl">
                <div className="text-red-500 mb-4">
                  <AlertCircle className="h-12 w-12 mx-auto mb-2" />
                  <p className="font-semibold mb-2">Error al generar vista previa</p>
                  <p className="text-sm">{error}</p>
                </div>

                {validationErrors.length > 0 && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
                    <p className="font-semibold text-red-700 mb-2">Errores de validación:</p>
                    <ul className="space-y-1 text-sm text-red-600">
                      {validationErrors.map((err, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-red-400 mt-0.5">•</span>
                          <span>{err}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {imageUrl && !loading && !error && (
              <img
                src={imageUrl}
                alt={chart?.name}
                className="max-w-full h-auto rounded"
              />
            )}
          </div>

          <div className="text-xs text-muted-foreground p-3 bg-muted/30 rounded">
            <p className="font-semibold mb-1">Nota:</p>
            <p>
              Esta vista previa usa valores de ejemplo. Los valores reales se calcularán
              cuando se genere el reporte con datos de submissions.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
